import type { DRUserEntry } from '../api/getMyGames';

/**
 * Lobby My Games abbreviations for prop-based Daily Reveal lines.
 * Keep these short — the scoreboard column sits beside this text.
 */
const PROP_STAT_ABBREV_BY_KEY: Record<string, string> = {
  receptions: 'REC',
  receiving_yards: 'REC YDS',
  rushing_yards: 'RUSH YDS',
  anytime_touchdown: 'ATD',
  passing_yards: 'PASS YDS',
  passing_touchdowns: 'PASS TD',
  all_purpose_yards: 'ALL YDS',
  sacks: 'SACKS',
};

/** Match free-text frozen labels ("RECEIVING YARDS", "Score a TD") to the same abbrevs. */
const PROP_STAT_ABBREV_BY_LABEL: Array<{ pattern: RegExp; abbrev: string }> = [
  {
    pattern: /\bpassing\s+tds?\b|\bpass\s+tds?\b|\bpass(?:ing)?\s+touchdowns?\b/i,
    abbrev: 'PASS TD',
  },
  { pattern: /\bany\s*time\s*t(?:ouch)?d(?:own)?s?\b|\bscore a td\b/i, abbrev: 'ATD' },
  { pattern: /\breceiving\s+yards?\b|\brec(?:eiving)?\s*yds?\b/i, abbrev: 'REC YDS' },
  { pattern: /\brushing\s+yards?\b|\brush\s*yds?\b/i, abbrev: 'RUSH YDS' },
  { pattern: /\bpassing\s+yards?\b|\bpass\s*yds?\b/i, abbrev: 'PASS YDS' },
  { pattern: /\ball[-\s]?purpose\s+yards?\b|\ball\s*yds?\b|\brush\s*\+\s*rec/i, abbrev: 'ALL YDS' },
  { pattern: /\breceptions?\b|\brecs?\b/i, abbrev: 'REC' },
  { pattern: /\bsacks?\b/i, abbrev: 'SACKS' },
];

function formatPropThreshold(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(1).replace(/\.0$/, '');
}

function abbreviatePropStat(raw: string | null | undefined): string {
  if (!raw) return '';
  const trimmed = raw.trim();
  if (!trimmed) return '';

  const asKey = trimmed.toLowerCase().replace(/\s+/g, '_');
  if (PROP_STAT_ABBREV_BY_KEY[asKey]) return PROP_STAT_ABBREV_BY_KEY[asKey];

  for (const { pattern, abbrev } of PROP_STAT_ABBREV_BY_LABEL) {
    if (pattern.test(trimmed)) return abbrev;
  }

  // Already short (e.g. "REC YDS") — keep as-is.
  return trimmed.toUpperCase();
}

/**
 * Parse a frozen label into threshold + raw stat text.
 * Accepts "OVER 82.5 RECEIVING YARDS", "86.5+ RUSH YDS", "Score a TD".
 */
function parseFrozenPropLabel(label: string): { threshold: number | null; statRaw: string } {
  const trimmed = label.trim();
  if (!trimmed) return { threshold: null, statRaw: '' };

  const overMatch = trimmed.match(/^over\s+([0-9]+(?:\.[0-9]+)?)\s+(.+)$/i);
  if (overMatch) {
    return { threshold: Number(overMatch[1]), statRaw: overMatch[2] };
  }

  const plusMatch = trimmed.match(/^([0-9]+(?:\.[0-9]+)?)\+\s*(.*)$/i);
  if (plusMatch) {
    return { threshold: Number(plusMatch[1]), statRaw: plusMatch[2] };
  }

  if (/^score a td$/i.test(trimmed) || /^anytime(?:\s+touchdown)?$/i.test(trimmed)) {
    return { threshold: 1, statRaw: 'anytime_touchdown' };
  }

  return { threshold: null, statRaw: trimmed };
}

export type LobbyPropPickParts = {
  /** Always leading-plus for overs, e.g. "+82.5". */
  value: string;
  /** Abbreviated stat, e.g. "REC YDS". */
  category: string;
};

/**
 * Compact Lobby My Games prop line: "+82.5 REC YDS" (never the word OVER).
 */
export function resolveLobbyPropPickParts(entry: DRUserEntry): LobbyPropPickParts | null {
  const frozen = entry.propDisplayLabel?.trim() || '';
  const parsed = frozen ? parseFrozenPropLabel(frozen) : { threshold: null, statRaw: '' };

  const threshold =
    entry.propThreshold != null && Number.isFinite(Number(entry.propThreshold))
      ? Number(entry.propThreshold)
      : parsed.threshold;

  const category = abbreviatePropStat(parsed.statRaw);
  if (threshold == null && !category) return null;

  // Anytime TD (and other binary 0.5 lines tagged ATD) surface as +1.
  const displayThreshold =
    threshold != null && category === 'ATD' && threshold <= 0.5 ? 1 : threshold;

  if (displayThreshold == null) {
    return category ? { value: '', category } : null;
  }

  return {
    value: `+${formatPropThreshold(displayThreshold)}`,
    category,
  };
}

/** Full single-line form used by Recent result copy. */
export function resolvePropPickLine(entry: DRUserEntry): string | null {
  const parts = resolveLobbyPropPickParts(entry);
  if (!parts) return null;
  if (parts.value && parts.category) return `${parts.value} ${parts.category}`;
  return parts.value || parts.category || null;
}

export function isPropBasedEntry(entry: DRUserEntry): boolean {
  return resolveLobbyPropPickParts(entry) != null;
}

export type LobbyPropResultLabel = {
  /** Primary status word: Won / Lost / Credited Win / Void / pending line. */
  statusLabel: string;
  /** Compact prop line under the status, e.g. "+39.5 REC YDS". */
  propLine: string | null;
  /**
   * Single-line fallback (status only, or pending prop line). Prefer rendering
   * `statusLabel` + `propLine` on two lines in the Recent card UI.
   */
  label: string;
  won: boolean;
  showIcon: boolean;
};

/**
 * Outcome / pending label for a prop pick on Recent cards.
 * Graded copy is status + compact prop line — never the recorded result value.
 */
export function formatLobbyPropResultLabel(
  entry: DRUserEntry,
  pendingLabel?: string
): LobbyPropResultLabel {
  const propLine = resolvePropPickLine(entry);
  const pending = pendingLabel ?? propLine ?? 'Pending';
  const status = entry.propResultStatus;

  switch (status) {
    case 'won':
      return {
        statusLabel: 'Won',
        propLine,
        label: 'Won',
        won: true,
        showIcon: true,
      };
    case 'credited':
      return {
        statusLabel: 'Credited Win',
        propLine,
        label: 'Credited Win',
        won: true,
        showIcon: true,
      };
    case 'void':
      return {
        statusLabel: 'Void',
        propLine,
        label: 'Void',
        won: false,
        showIcon: true,
      };
    case 'lost':
      return {
        statusLabel: 'Lost',
        propLine,
        label: 'Lost',
        won: false,
        showIcon: true,
      };
    case 'pending':
    default:
      return {
        statusLabel: pending,
        propLine: null,
        label: pending,
        won: false,
        showIcon: false,
      };
  }
}
